import { type ReactNode, useEffect, useState } from 'react'
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom'
import {
  ArrowLeft,
  ChevronRight,
  ClipboardCheck,
  GraduationCap,
  History,
  Mail,
  MessageSquare,
  Phone,
} from 'lucide-react'
import { Badge, Button, Card } from '@/components/ui'
import { PartnerPriorityReviewRequestCard } from '@/components/pedagogy/PartnerPriorityReviewRequestCard'
import { useStudioStore } from '@/store'
import {
  getPartnerCityTeacherDetail,
  type PartnerTeacherAttestationResult,
  type PartnerTeacherDetail,
  type PartnerTeacherReviewSource,
  type PartnerTeacherStatus,
  type PartnerTeacherTraining,
  type PartnerTeacherTrainingAttestationAttempt,
} from '@/services/partnerTeachersService'
import { listPartnerPedagogyRequestedReviewKeys } from '@/services/pedagogyPriorityRequestService'
import { getSeniorTeacherReviewContext } from '@/services/seniorTeacherReviewService'

const INITIAL_REVIEWS_COUNT = 5
const REVIEWS_PAGE_SIZE = 5

const statusLabels: Record<PartnerTeacherStatus, string> = {
  candidate: 'Кандидат',
  training: 'Обучение',
  working: 'Работает',
  dismissed: 'Уволен',
}

const qualificationLabels: Record<string, string> = {
  stretch_pilates: 'Растяжка и пилатес',
  universal: 'Универсал',
}

function canViewTeachers(accessKind: string | undefined): boolean {
  return accessKind === 'owner' || accessKind === 'manager' || accessKind === 'management_company' || accessKind === 'senior_teacher'
}

function statusVariant(status: PartnerTeacherStatus): 'success' | 'warning' | 'neutral' {
  if (status === 'working') return 'success'
  if (status === 'candidate' || status === 'training') return 'warning'
  return 'neutral'
}

function teacherInitials(fullName: string): string {
  return fullName
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map(part => part[0]?.toUpperCase() || '')
    .join('') || 'П'
}

function asDate(value: string): Date | null {
  const normalized = value.includes('T') ? value : `${value}T00:00:00`
  const date = new Date(normalized)
  return Number.isNaN(date.getTime()) ? null : date
}

function formatDate(value: string | null, year = false): string {
  if (!value) return '—'
  const date = asDate(value)
  if (!date) return value
  return new Intl.DateTimeFormat('ru-RU', {
    day: 'numeric',
    month: 'long',
    ...(year ? { year: 'numeric' as const } : {}),
  }).format(date)
}

function formatCompactDate(value: string | null): string {
  if (!value) return '—'
  const date = asDate(value)
  if (!date) return value
  return new Intl.DateTimeFormat('ru-RU', { day: 'numeric', month: 'short' }).format(date)
}

function formatTrainingDateTime(value: string | null): string {
  if (!value) return 'Дата и время не указаны'
  const date = asDate(value)
  if (!date) return value
  const dateText = new Intl.DateTimeFormat('ru-RU', { day: 'numeric', month: 'long' }).format(date)
  if (!value.includes('T')) return dateText
  const timeText = new Intl.DateTimeFormat('ru-RU', { hour: '2-digit', minute: '2-digit' }).format(date)
  return `${dateText} · ${timeText}`
}

function formatPercent(value: number | null): string {
  return value == null ? '—' : `${Math.round(value)}%`
}

function reviewConclusion(qualityPercent: number): string {
  if (qualityPercent >= 100) return 'Регламент соблюден полностью'
  if (qualityPercent < 50) return 'Тренеру необходимо записаться на пересдачу регламента'
  return 'Регламент выполнен частично'
}

function resultLabel(result: PartnerTeacherAttestationResult | null): string {
  if (result === 'passed') return 'Сдана'
  if (result === 'retake') return 'Пересдача'
  if (result === 'refused') return 'Не сдана'
  return 'Ожидается'
}

function resultVariant(result: PartnerTeacherAttestationResult | null): 'success' | 'warning' | 'error' | 'neutral' {
  if (result === 'passed') return 'success'
  if (result === 'retake') return 'warning'
  if (result === 'refused') return 'error'
  return 'neutral'
}

function trainingStatusLabel(training: PartnerTeacherTraining): string {
  if (training.status === 'completed') return 'Обучение успешно завершено'
  if (training.status === 'closed') return 'Цикл обучения завершён'
  return 'Обучение продолжается'
}

function trainingPeriod(training: PartnerTeacherTraining): string | null {
  const from = training.startDate ? formatDate(training.startDate, true) : null
  const finishedAt = training.completedAt || training.closedAt
  const to = finishedAt ? formatDate(finishedAt, true) : null
  if (from && to) return `${from} — ${to}`
  if (from) return `Начато ${from}`
  if (to) return `Завершено ${to}`
  return null
}

export function PartnerTeacherDetailPage() {
  const { partnerNumber = '', studioId = '', teacherId = '' } = useParams<{
    partnerNumber: string
    studioId: string
    teacherId: string
  }>()
  const navigate = useNavigate()
  const location = useLocation()
  const { studios, studiosSynced } = useStudioStore()
  const parsedStudioId = Number(studioId)
  const studio = studios.find(item => item.studio_internal_id === parsedStudioId)
  const isSeniorTeacher = studio?.role === 'senior_teacher' || studio?.access_kind === 'senior_teacher'
  const hasAccess = studio ? canViewTeachers(studio.access_kind) || studio.role === 'manager' || isSeniorTeacher : false

  const [detail, setDetail] = useState<PartnerTeacherDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [visibleReviewCount, setVisibleReviewCount] = useState(INITIAL_REVIEWS_COUNT)
  const [reviewSourceFilter, setReviewSourceFilter] = useState<'all' | PartnerTeacherReviewSource>('all')
  const [requestedReviewKeys, setRequestedReviewKeys] = useState<Set<string>>(new Set())
  const [currentSeniorTeacherId, setCurrentSeniorTeacherId] = useState<string | null>(null)

  useEffect(() => {
    setVisibleReviewCount(INITIAL_REVIEWS_COUNT)
    setReviewSourceFilter('all')
  }, [teacherId])

  useEffect(() => {
    setVisibleReviewCount(INITIAL_REVIEWS_COUNT)
  }, [reviewSourceFilter])

  useEffect(() => {
    if (!Number.isInteger(parsedStudioId) || parsedStudioId <= 0 || !teacherId || !hasAccess) {
      setLoading(false)
      return
    }

    let cancelled = false
    setLoading(true)
    setError('')

    Promise.all([
      getPartnerCityTeacherDetail(parsedStudioId, teacherId),
      (studio?.access_kind === 'owner' || studio?.role === 'manager' || isSeniorTeacher)
        ? listPartnerPedagogyRequestedReviewKeys(parsedStudioId, teacherId).catch(() => [])
        : Promise.resolve([] as string[]),
      isSeniorTeacher
        ? getSeniorTeacherReviewContext(parsedStudioId).then(context => context.reviewerTeacherId).catch(() => null)
        : Promise.resolve(null),
    ])
      .then(([data, requestReviewKeys, reviewerTeacherId]) => {
        if (cancelled) return
        setDetail(data)
        setRequestedReviewKeys(new Set(requestReviewKeys))
        setCurrentSeniorTeacherId(reviewerTeacherId)
      })
      .catch(loadError => {
        if (!cancelled) setError(loadError instanceof Error ? loadError.message : 'Не удалось загрузить карточку педагога')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [hasAccess, isSeniorTeacher, parsedStudioId, studio?.access_kind, studio?.role, teacherId])

  const basePath = `/partners/${partnerNumber}/studios/${studioId}/teachers`

  if (studiosSynced && (!studio || !hasAccess)) {
    return <Navigate to={`/partners/${partnerNumber}/studios/${studioId}/analytics`} replace />
  }

  if (loading) {
    return <Card className="border border-border text-sm text-muted-foreground">Загружаем карточку педагога…</Card>
  }

  if (error) {
    return (
      <div className="space-y-3 pb-24">
        <Button variant="outline" size="sm" onClick={() => navigate(basePath)}>
          <ArrowLeft className="mr-1.5 inline h-4 w-4" />
          К педагогам
        </Button>
        <Card className="border border-error/30 bg-error/5 text-sm text-error">{error}</Card>
      </div>
    )
  }

  if (!detail) {
    return (
      <div className="space-y-3 pb-24">
        <Button variant="outline" size="sm" onClick={() => navigate(basePath)}>
          <ArrowLeft className="mr-1.5 inline h-4 w-4" />
          К педагогам
        </Button>
        <Card className="border border-border text-sm text-muted-foreground">
          {studio?.access_kind === 'senior_teacher'
            ? 'Педагог не найден у этого франчайзи.'
            : 'Педагог не найден в команде этого города.'}
        </Card>
      </div>
    )
  }

  const filteredReviews = detail.reviews.filter(review =>
    reviewSourceFilter === 'all' || review.reviewSource === reviewSourceFilter
  )
  const visibleReviews = filteredReviews.slice(0, visibleReviewCount)
  const remainingReviews = Math.max(0, filteredReviews.length - visibleReviews.length)
  const teacherCities = detail.cityNames.length > 0
    ? detail.cityNames
    : detail.scope.cityName && detail.scope.cityName !== 'Все города'
      ? [detail.scope.cityName]
      : []

  return (
    <div className="space-y-4 pb-24">
      <button
        type="button"
        onClick={() => navigate(basePath)}
        className="inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" />
        Педагоги
      </button>

      <Card className="border border-border shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex min-w-0 items-start gap-4">
            <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-primary/15 text-lg font-semibold">
              {teacherInitials(detail.fullName)}
            </div>
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="text-xl font-bold tracking-tight sm:text-2xl">{detail.fullName}</h1>
                <Badge variant={statusVariant(detail.status)}>{statusLabels[detail.status]}</Badge>
                {detail.isSeniorTeacher && <Badge variant="warning" className="px-2.5 py-1 text-xs">Старший педагог</Badge>}
              </div>
              <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-muted-foreground">
                <span>{detail.qualification ? qualificationLabels[detail.qualification] : 'Направление не указано'}</span>
                {teacherCities.length > 0 && (
                  <>
                    <span className="text-border">·</span>
                    <span>{teacherCities.join(' · ')}</span>
                  </>
                )}
              </div>
              {(detail.contactPhone || detail.contactEmail || detail.telegramUsername) && (
                <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-muted-foreground">
                  {detail.contactPhone && (
                    <a href={`tel:${detail.contactPhone}`} className="inline-flex items-center gap-1 hover:text-foreground hover:underline">
                      <Phone className="h-3.5 w-3.5" />
                      {detail.contactPhone}
                    </a>
                  )}
                  {detail.contactEmail && (
                    <>
                      {detail.contactPhone && <span className="text-border">·</span>}
                      <a href={`mailto:${detail.contactEmail}`} className="inline-flex items-center gap-1 hover:text-foreground hover:underline">
                        <Mail className="h-3.5 w-3.5" />
                        {detail.contactEmail}
                      </a>
                    </>
                  )}
                  {detail.telegramUsername && (
                    <>
                      {(detail.contactPhone || detail.contactEmail) && <span className="text-border">·</span>}
                      <a
                        href={`https://t.me/${detail.telegramUsername.replace(/^@/, '')}`}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 hover:text-foreground hover:underline"
                      >
                        <MessageSquare className="h-3.5 w-3.5" />
                        @{detail.telegramUsername.replace(/^@/, '')}
                      </a>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="flex flex-col gap-2 sm:flex-row sm:items-center lg:shrink-0">
            <div className="grid gap-2 sm:grid-cols-2 lg:w-[520px]">
              <ReviewSourceMetric
                label="УК"
                count={detail.managementCompanyReviewsCount}
                quality={detail.managementCompanyAverageQualityPercent ?? detail.managementCompanyLatestReviewQualityPercent}
                latestReviewDate={detail.managementCompanyLatestReviewDate}
              />
              <ReviewSourceMetric
                label="Старший педагог"
                count={detail.seniorTeacherReviewsCount}
                quality={detail.seniorTeacherAverageQualityPercent ?? detail.seniorTeacherLatestReviewQualityPercent}
                latestReviewDate={detail.seniorTeacherLatestReviewDate}
              />
            </div>
          </div>
        </div>
      </Card>

      <div className="grid gap-4 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] lg:items-start">
        <div className="space-y-4">
          {detail.status === 'dismissed' ? (
            <Card className="border border-border shadow-sm">
              <div className="font-semibold">Уволен у этого франчайзи</div>
              <div className="mt-2 text-sm leading-6 text-muted-foreground">
                Текущие данные другого франчайзи здесь не показываются. История отсмотров по студиям этого франчайзи остаётся доступна.
              </div>
            </Card>
          ) : (
            <TrainingCard training={detail.training} />
          )}

        </div>

        <ReviewsCard
          detail={detail}
          reviews={visibleReviews}
          remaining={remainingReviews}
          sourceFilter={reviewSourceFilter}
          onSourceFilterChange={setReviewSourceFilter}
          requestedReviewKeys={requestedReviewKeys}
          requestControl={detail.status !== 'dismissed' ? (
            <div className="flex flex-wrap items-center justify-end gap-2">
              {studio?.access_kind === 'senior_teacher'
                && currentSeniorTeacherId !== null
                && currentSeniorTeacherId !== detail.teacherId && (
                  <Button
                    className="h-10 shrink-0 whitespace-nowrap rounded-lg px-4 text-sm font-semibold"
                    onClick={() => navigate(`${basePath}/reviews/new?teacherId=${encodeURIComponent(detail.teacherId)}`)}
                  >
                    <ClipboardCheck className="mr-1.5 inline h-4 w-4" />
                    Новый отсмотр
                  </Button>
                )}
              {(studio?.access_kind === 'owner' || studio?.role === 'manager' || studio?.access_kind === 'senior_teacher') && (
                <PartnerPriorityReviewRequestCard
                  studioInternalId={parsedStudioId}
                  partnerNumber={partnerNumber}
                  preselectedTeacherId={detail.teacherId}
                  completedTarget="review"
                  presentation="toolbar"
                />
              )}
            </div>
          ) : null}
          onOpenReview={reviewKey => {
            const returnTo = `${location.pathname}${location.search}`
            navigate(`${basePath}/${detail.teacherId}/reviews/${reviewKey}?returnTo=${encodeURIComponent(returnTo)}`)
          }}
          onShowMore={() => setVisibleReviewCount(current => current + REVIEWS_PAGE_SIZE)}
        />
      </div>
    </div>
  )
}

function ReviewSourceMetric({
  label,
  count,
  quality,
  latestReviewDate,
}: {
  label: string
  count: number
  quality: number | null
  latestReviewDate: string | null
}) {
  return (
    <div className="rounded-lg border border-border bg-muted/25 px-3 py-2.5">
      <div className="text-xs font-medium text-muted-foreground">{label}</div>
      <div className="mt-1 flex items-baseline justify-between gap-3">
        <div className="text-lg font-semibold tabular-nums">{formatPercent(quality)}</div>
        <div className="text-xs text-muted-foreground">Отсмотров: <span className="font-medium text-foreground">{count}</span></div>
      </div>
      <div className="mt-1 text-[11px] text-muted-foreground">Последний: {formatCompactDate(latestReviewDate)}</div>
    </div>
  )
}

function reviewSourceLabel(source: PartnerTeacherReviewSource): string {
  return source === 'senior_teacher' ? 'Старший педагог' : 'УК'
}

function reviewSourcePillClass(source: PartnerTeacherReviewSource): string {
  return source === 'senior_teacher'
    ? 'border-primary/30 bg-primary/10 text-primary'
    : 'border-info/30 bg-info/10 text-info'
}

function reviewSourceFilterClass(
  source: 'all' | PartnerTeacherReviewSource,
  active: boolean
): string {
  if (source === 'management_company') {
    return active
      ? 'border-info bg-info text-white shadow-sm'
      : 'border-info/25 bg-info/5 text-info hover:bg-info/10'
  }
  if (source === 'senior_teacher') {
    return active
      ? 'border-primary bg-primary text-primary-foreground shadow-sm'
      : 'border-primary/25 bg-primary/5 text-primary hover:bg-primary/10'
  }
  return active
    ? 'border-foreground bg-foreground text-background shadow-sm'
    : 'border-border bg-background text-muted-foreground hover:bg-muted/60 hover:text-foreground'
}

function TrainingCard({ training }: { training: PartnerTeacherTraining | null }) {
  if (!training) {
    return (
      <Card className="border border-border shadow-sm">
        <div className="flex items-center gap-2">
          <GraduationCap className="h-4 w-4 text-primary" />
          <h2 className="font-semibold">Обучение</h2>
        </div>
        <div className="mt-3 text-sm text-muted-foreground">Данные об обучении не указаны.</div>
      </Card>
    )
  }

  const attempts = training.attestationAttempts ?? []
  const hasRetakes = attempts.some(attempt => attempt.attemptNo > 1)
  const period = trainingPeriod(training)

  return (
    <Card className="border border-border shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <div className="flex items-center gap-2">
            <GraduationCap className="h-4 w-4 text-primary" />
            <h2 className="font-semibold">Обучение</h2>
          </div>
          <div className="mt-2 text-sm font-medium">{trainingStatusLabel(training)}</div>
          {period && <div className="mt-0.5 text-xs text-muted-foreground">{period}</div>}
        </div>
      </div>

      <div className="mt-3 grid grid-cols-3 gap-2">
        <AttestationTile number={1} date={training.firstAttestationDate} result={training.firstAttestationResult} />
        <AttestationTile number={2} date={training.secondAttestationDate} result={training.secondAttestationResult} />
        <AttestationTile number={3} date={training.thirdAttestationDate} result={training.thirdAttestationResult} />
      </div>

      {hasRetakes && (
        <details className="group mt-3 border-t border-border/70 pt-3">
          <summary className="flex cursor-pointer list-none items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-foreground">
            <History className="h-3.5 w-3.5" />
            История пересдач
          </summary>
          <AttestationHistory attempts={attempts} />
        </details>
      )}
    </Card>
  )
}

function AttestationTile({
  number,
  date,
  result,
}: {
  number: 1 | 2 | 3
  date: string | null
  result: PartnerTeacherAttestationResult | null
}) {
  return (
    <div className="min-w-0 rounded-lg border border-border/70 bg-muted/20 px-2.5 py-2.5">
      <div className="text-[11px] font-medium text-muted-foreground">{number}-я аттестация</div>
      <div className="mt-1 truncate text-sm font-semibold">{date ? formatCompactDate(date) : '—'}</div>
      <Badge variant={resultVariant(result)} className="mt-1.5 max-w-full px-2 py-0.5 text-[10px]">
        {resultLabel(result)}
      </Badge>
    </div>
  )
}

function AttestationHistory({ attempts }: { attempts: PartnerTeacherTrainingAttestationAttempt[] }) {
  const ordered = [...attempts].sort((left, right) => {
    if (left.stage !== right.stage) return left.stage - right.stage
    if (left.attemptNo !== right.attemptNo) return left.attemptNo - right.attemptNo
    return (left.attestationDate ?? left.createdAt).localeCompare(right.attestationDate ?? right.createdAt)
  })

  return (
    <div className="mt-2 divide-y divide-border/60 rounded-lg border border-border/70 bg-muted/15 px-3">
      {ordered.map(attempt => (
        <div key={attempt.attemptId} className="flex items-center justify-between gap-3 py-2 text-xs">
          <div className="min-w-0">
            <span className="font-medium">{attempt.stage}-я · попытка {attempt.attemptNo}</span>
            <span className="ml-2 text-muted-foreground">{formatCompactDate(attempt.attestationDate)}</span>
          </div>
          <Badge variant={resultVariant(attempt.result)} className="shrink-0 px-2 py-0.5 text-[10px]">
            {resultLabel(attempt.result)}
          </Badge>
        </div>
      ))}
    </div>
  )
}

function ReviewsCard({
  detail,
  reviews,
  remaining,
  sourceFilter,
  onSourceFilterChange,
  requestedReviewKeys,
  requestControl,
  onOpenReview,
  onShowMore,
}: {
  detail: PartnerTeacherDetail
  reviews: PartnerTeacherDetail['reviews']
  remaining: number
  sourceFilter: 'all' | PartnerTeacherReviewSource
  onSourceFilterChange: (source: 'all' | PartnerTeacherReviewSource) => void
  requestedReviewKeys: Set<string>
  requestControl?: ReactNode
  onOpenReview: (reviewKey: string) => void
  onShowMore: () => void
}) {
  return (
    <Card className="overflow-hidden border border-border !p-0 shadow-sm">
      <div className="border-b border-border px-4 py-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="font-semibold">Отсмотры</h2>
            <div className="mt-0.5 text-xs text-muted-foreground">Единая история · всего {detail.reviewsCount}</div>
          </div>
          {requestControl && <div className="shrink-0">{requestControl}</div>}
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          {[
            { value: 'all' as const, label: 'Все' },
            { value: 'management_company' as const, label: 'УК' },
            { value: 'senior_teacher' as const, label: 'Старший педагог' },
          ].map(option => (
            <button
              key={option.value}
              type="button"
              onClick={() => onSourceFilterChange(option.value)}
              className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${reviewSourceFilterClass(
                option.value,
                sourceFilter === option.value
              )}`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      {reviews.length > 0 ? (
        <div className="space-y-2 p-3">
          {reviews.map(review => (
            <button
              key={review.reviewKey}
              type="button"
              onClick={() => onOpenReview(review.reviewKey)}
              className="flex w-full items-start gap-3 rounded-lg border border-border/70 bg-background px-3 py-3 text-left transition-colors hover:border-primary/30 hover:bg-muted/30"
            >
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                  <span className="text-sm font-medium">Тренировка · {formatTrainingDateTime(review.trainingAt)}</span>
                  {review.directionName && (
                    <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] text-muted-foreground">{review.directionName}</span>
                  )}
                </div>
                <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
                  <span>Отсмотр · {formatDate(review.reviewDate, true)}</span>
                  <span className={`rounded-full border px-2 py-0.5 text-[11px] font-medium ${reviewSourcePillClass(review.reviewSource)}`}>
                    {reviewSourceLabel(review.reviewSource)}
                  </span>
                  {review.reviewSource === 'senior_teacher' && review.reviewerName && review.reviewerName !== '—' && (
                    <span>Проверил: <span className="font-medium text-foreground">{review.reviewerName}</span></span>
                  )}
                  {review.reviewSource === 'management_company' && requestedReviewKeys.has(review.reviewKey) && (
                    <span className="rounded-full border border-primary/25 bg-primary/5 px-2 py-0.5 text-[11px] font-medium text-primary">
                      По вашему запросу
                    </span>
                  )}
                </div>
                <div className="mt-1 line-clamp-1 text-xs leading-5 text-muted-foreground">
                  {reviewConclusion(review.qualityPercent)}
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-2 pt-0.5">
                <span className="text-base font-semibold tabular-nums">{formatPercent(review.qualityPercent)}</span>
                <ChevronRight className="h-4 w-4 text-muted-foreground" />
              </div>
            </button>
          ))}
        </div>
      ) : (
        <div className="px-4 py-8 text-center text-sm text-muted-foreground">
          Доступных отсмотров пока нет.
        </div>
      )}

      {remaining > 0 && (
        <button
          type="button"
          onClick={onShowMore}
          className="w-full border-t border-border px-4 py-3 text-left text-xs font-medium text-primary transition-colors hover:bg-muted/30"
        >
          Показать ещё {Math.min(REVIEWS_PAGE_SIZE, remaining)}
          <span className="ml-1 text-muted-foreground">· осталось {remaining}</span>
        </button>
      )}
    </Card>
  )
}
